import { Category, Brand, Product, Banner, Coupon, SiteSettings, Review, PolicySection } from '../types';
import { generateFullCatalogProducts } from './productGenerator';
const womenBannerImg = 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=80';
const menBannerImg = 'https://images.unsplash.com/photo-1597983073493-88cd35cf93b0?auto=format&fit=crop&w=1200&q=80';
const kidsBannerImg = 'https://images.unsplash.com/photo-1503919545889-aef636e10ad4?auto=format&fit=crop&w=1200&q=80';
const innerwearBannerImg = '/images/innerwear_department_new.png';

export const DEFAULT_PRIVACY_SECTIONS: PolicySection[] = [
  {
    id: 'intro',
    title: 'Privacy Policy Overview',
    content: 'At PGmart, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, disclose, and safeguard your data when you visit our website or make a purchase.'
  },
  {
    id: 'info-collected',
    title: '1. Information We Collect',
    content: 'We collect information you provide directly to us when you create an account, place an order, contact customer support, or interact with our services. This includes your name, email address, shipping address, phone number, and order history.'
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    content: '• To process and deliver your orders and manage shipping logistics.\n• To send order confirmations, delivery tracking updates, and OTP authentications.\n• To provide customer support and respond to inquiries.\n• To personalize your shopping experience and improve our platform.'
  },
  {
    id: 'data-protection',
    title: '3. Data Protection & Security',
    content: 'We implement industry-standard technical and organizational security measures to protect your personal information against unauthorized access, loss, or misuse. We never sell your personal data to third parties.'
  },
  {
    id: 'payment-security',
    title: '4. Payment Information & Encryption',
    content: 'All payment transactions are processed securely through certified payment gateways (Razorpay, UPI, Cards). We do not store your complete credit/debit card numbers or sensitive banking credentials on our servers.'
  },
  {
    id: 'cookies',
    title: '5. Cookies & Browsing Preferences',
    content: 'We use cookies and similar technologies to enhance your browsing experience, remember your cart items, and understand how visitors use our store.'
  },
  {
    id: 'shipping-returns',
    title: '6. Shipping & Return Guidelines',
    content: 'Orders are dispatched within 24–48 hours via Delhivery / BlueDart partners with an estimated delivery of 2–6 business days. Products are eligible for return within 7–15 days of delivery, provided they are unused and unwashed with tags intact.'
  },
  {
    id: 'contact',
    title: '7. Contact & Privacy Inquiries',
    content: 'If you have any questions or requests regarding your data and privacy, please reach out to our Customer Care team via phone or email.'
  }
];

export function parsePrivacySections(raw: string | undefined | null): PolicySection[] {
  if (!raw || typeof raw !== 'string' || !raw.trim()) {
    return DEFAULT_PRIVACY_SECTIONS;
  }
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0].title === 'string') {
      return parsed;
    }
  } catch (e) {
    // If it's a plain string, convert to single section
    return [
      {
        id: 'section-1',
        title: 'Privacy Policy & Terms',
        content: raw
      }
    ];
  }
  return DEFAULT_PRIVACY_SECTIONS;
}

export const initialSiteSettings: SiteSettings = {
  storeName: "PGmart",
  tagline: "India's Most Trusted Choice for Quality & Style",
  logoUrl: "/src/assets/images/pgmart_logo_new.png",
  faviconUrl: "/favicon.png",
  primaryColor: "#C0654B", // Rose Clay / Terracotta
  primaryDarkColor: "#8B4A38",
  bgMutedColor: "#F3E9E4", // Soft Rose Tint
  secondaryDarkColor: "#2B2620",
  fontFamily: "Poppins, sans-serif",
  contactEmail: "support@pgmart.in",
  contactPhone: "+91 94711 55434",
  supportEmail: "support@pgmart.in",
  supportPhone: "+91 94711 55434",
  whatsappNumber: "9471155434",
  address: "Kapda Patti, Jharia, Dhanbad, Jharkhand 828111",
  currencySymbol: "₹",
  currencyCode: "INR",
  freeShippingThreshold: 999,
  standardShippingFee: 79,
  codFee: 49,
  codEnabled: true,
  stripeEnabled: true,
  razorpayEnabled: true,
  razorpayKeyId: "rzp_live_TQsQotOvsIaMs4",
  stripePublicKey: "pk_test_TC987654321",
  gstNumber: "19AAAAA0000A1Z5",
  dealsEnabled: true,
  dealsTitle: "Deals of the Day",
  dealsTimerHours: 14,
  dealsTimerMinutes: 22,
  dealsMinDiscount: 20,
  adBannerEnabled: true,
  newArrivalsEnabled: true,
  newArrivalsTitle: "New Arrivals",
  newArrivalsSubtitle: "Explore the latest ethnic wear, designer sarees, & festive drops",
  newArrivalsBadge: "JUST ARRIVED",
  newArrivalsMaxItems: 10,
  orderConfirmationEmailEnabled: true,
  orderDeliveredEmailEnabled: true,
  delhiveryEnabled: false,
  delhiveryApiKey: "",
  delhiveryClientName: "PGMART",
  delhiveryPickupLocation: "PGmart Kapda Patti Warehouse",
  delhiveryPickupPincode: "828111",
  delhiverySandbox: true,
};

export const initialBrands: Brand[] = [
  { id: 'b1', name: 'Manyavar', slug: 'manyavar', logo: '/images/brands/manyavar.svg', description: 'Royal Ethnic & Sherwanis', isFeatured: true, isActive: true },
  { id: 'b2', name: 'Biba', slug: 'biba', logo: '/images/brands/biba.svg', description: "Women's Ethnic & Anarkalis", isFeatured: true, isActive: true },
  { id: 'b3', name: 'W for Woman', slug: 'w-for-woman', logo: '/images/brands/w-for-woman.svg', description: 'Contemporary Indo-Western Wear', isFeatured: true, isActive: true },
  { id: 'b4', name: 'FabIndia', slug: 'fabindia', logo: '/images/brands/fabindia.svg', description: 'Handwoven Silk & Cotton Outfits', isFeatured: true, isActive: true },
  { id: 'b5', name: 'Aurelia', slug: 'aurelia', logo: '/images/brands/aurelia.svg', description: 'Designer Kurti & Ethnic Sets', isFeatured: true, isActive: true },
  { id: 'b6', name: 'Allen Solly', slug: 'allen-solly', logo: '/images/brands/allen-solly.svg', description: 'Modern Workwear & Executive Wear', isFeatured: true, isActive: true },
  { id: 'b7', name: 'Van Heusen', slug: 'van-heusen', logo: '/images/brands/van-heusen.svg', description: 'Premium Formal & Corporate Apparel', isFeatured: true, isActive: true },
  { id: 'b8', name: 'Raymond', slug: 'raymond', logo: '/images/brands/raymond.svg', description: 'The Complete Man Heritage Suiting', isFeatured: true, isActive: true },
  { id: 'b9', name: 'Peter England', slug: 'peter-england', logo: '/images/brands/peter-england.svg', description: 'Classic Formal & Casual Menswear', isFeatured: true, isActive: true },
  { id: 'b10', name: 'Blackberrys', slug: 'blackberrys', logo: '/images/brands/blackberrys.svg', description: 'Sharp Suits & Executive Trousers', isFeatured: true, isActive: true },
  { id: 'b11', name: 'Mufti', slug: 'mufti', logo: '/images/brands/mufti.svg', description: 'Urban Denim & Streetwear Fashion', isFeatured: true, isActive: true },
  { id: 'b12', name: 'Monte Carlo', slug: 'monte-carlo', logo: '/images/brands/monte-carlo.svg', description: 'Luxe Woollens & Seasonal Apparel', isFeatured: true, isActive: true },
  { id: 'b13', name: "Levi's", slug: 'levis', logo: '/images/brands/levis.svg', description: 'Original Denim & Casual Wear', isFeatured: true, isActive: true },
  { id: 'b14', name: 'U.S. Polo Assn.', slug: 'us-polo-assn', logo: '/images/brands/us-polo-assn.svg', description: 'Authentic Sporty Lifestyle Wear', isFeatured: true, isActive: true },
  { id: 'b15', name: 'Pepe Jeans', slug: 'pepe-jeans', logo: '/images/brands/pepe-jeans.svg', description: 'Casual Denim & Outerwear Apparel', isFeatured: true, isActive: true },
  { id: 'b16', name: "Neeru's", slug: 'neerus', logo: '/images/brands/neerus.svg', description: 'Royal Ethnic & Bridal Sarees', isFeatured: true, isActive: true },
  { id: 'b17', name: 'Craftsvilla', slug: 'craftsvilla', logo: '/images/brands/craftsvilla.svg', description: 'Traditional Artisan Ethnic Apparel', isFeatured: true, isActive: true },
  { id: 'b18', name: 'Mohey', slug: 'mohey', logo: '/images/brands/mohey.svg', description: 'Luxury Bridal & Festival Lehengas', isFeatured: true, isActive: true },
  { id: 'b19', name: 'Sabhyata', slug: 'sabhyata', logo: '/images/brands/sabhyata.svg', description: 'Ethnic Kurti Collections', isFeatured: true, isActive: true },
  { id: 'b20', name: 'Jockey', slug: 'jockey', logo: '/images/brands/jockey.svg', description: 'Premium Loungewear & Innerwear', isFeatured: true, isActive: true },
  { id: 'b21', name: 'Sparky Jeans', slug: 'sparky-jeans', logo: '/images/brands/sparky-jeans.svg', description: 'Authentic Indian Jeanswear & Denim', isFeatured: true, isActive: true },
  { id: 'b22', name: 'Catmint', slug: 'catmint', logo: '/images/brands/catmint.svg', description: 'Kids & Festive Ethnic Apparel', isFeatured: true, isActive: true },
  { id: 'b23', name: 'Flu Jeans', slug: 'flu-jeans', logo: '/images/brands/flu-jeans.svg', description: 'Youth Denim & Street Casuals', isFeatured: true, isActive: true },
  { id: 'b24', name: 'Bad Boyz', slug: 'bad-boyz', logo: '/images/brands/bad-boyz.svg', description: 'Boys & Teens Urban Streetwear', isFeatured: true, isActive: true },
  { id: 'b25', name: 'LoLiPoLi', slug: 'lolipoli', logo: '/images/brands/lolipoli.svg', description: 'Playful Kids & Toddler Everyday Wear', isFeatured: true, isActive: true },
  { id: 'b26', name: 'Tulip', slug: 'tulip', logo: '/images/brands/tulip.svg', description: 'Traditional Handcrafted Ethnic Artistry', isFeatured: true, isActive: true },
  { id: 'b27', name: 'PGmart Heritage', slug: 'pgmart', logo: '/images/brands/pgmart.svg', description: 'Signature Heritage Sarees & Weaves', isFeatured: true, isActive: true },
];

export const initialCategories: Category[] = [
  {
    id: 'women',
    name: "Women's Fashion",
    slug: 'women',
    image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80',
    banner: womenBannerImg,
    status: 'active',
    sortOrder: 1,
    subcategories: [
      {
        id: 'w-ethnic',
        categoryId: 'women',
        name: 'Ethnic & Traditional Wear',
        slug: 'women-ethnic',
        status: 'active',
        image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80',
        types: [
          { id: 'wt-saree', subcategoryId: 'w-ethnic', name: 'Saree (Banarasi, Chiffon, Georgette)', slug: 'saree', status: 'active' },
          { id: 'wt-salwar', subcategoryId: 'w-ethnic', name: 'Salwar Kameez & Churidar', slug: 'salwar-kameez', status: 'active' },
          { id: 'wt-lehenga', subcategoryId: 'w-ethnic', name: 'Lehenga Choli', slug: 'lehenga-choli', status: 'active' },
          { id: 'wt-anarkali', subcategoryId: 'w-ethnic', name: 'Anarkali Suits', slug: 'anarkali-suits', status: 'active' },
          { id: 'wt-kurti', subcategoryId: 'w-ethnic', name: 'Kurti & Kurta Sets', slug: 'kurtis', status: 'active' },
          { id: 'wt-palazzo', subcategoryId: 'w-ethnic', name: 'Palazzo & Sharara Suits', slug: 'palazzo-suits', status: 'active' },
        ]
      },
      {
        id: 'w-western',
        categoryId: 'women',
        name: 'Western Wear',
        slug: 'women-western',
        status: 'active',
        image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=800&q=80',
        types: [
          { id: 'wt-dresses', subcategoryId: 'w-western', name: 'Dresses (Maxi, Midi, Party)', slug: 'dresses', status: 'active' },
          { id: 'wt-tops', subcategoryId: 'w-western', name: 'Tops, Blouses & Crop Tops', slug: 'tops-blouses', status: 'active' },
          { id: 'wt-jeans', subcategoryId: 'w-western', name: 'Jeans & Denim Pants', slug: 'women-jeans', status: 'active' },
          { id: 'wt-trousers', subcategoryId: 'w-western', name: 'Trousers & Formal Pants', slug: 'women-trousers', status: 'active' },
          { id: 'wt-coord', subcategoryId: 'w-western', name: 'Co-ord Sets & Jumpsuits', slug: 'coord-sets', status: 'active' },
          { id: 'wt-skirts', subcategoryId: 'w-western', name: 'Skirts (Mini, Midi, Pencil)', slug: 'skirts', status: 'active' },
        ]
      },
      {
        id: 'w-formal',
        categoryId: 'women',
        name: 'Formal & Office Wear',
        slug: 'women-formal',
        status: 'active',
        types: [
          { id: 'wt-blazers', subcategoryId: 'w-formal', name: 'Blazers & Suits', slug: 'women-blazers', status: 'active' },
          { id: 'wt-formal-shirts', subcategoryId: 'w-formal', name: 'Formal Shirts & Trousers', slug: 'women-formal-shirts', status: 'active' },
        ]
      },
      {
        id: 'w-innerwear',
        categoryId: 'women',
        name: 'Lingerie & Innerwear',
        slug: 'women-innerwear',
        status: 'active',
        types: [
          { id: 'wt-bras', subcategoryId: 'w-innerwear', name: 'Bras (Padded, Sports, Seamless)', slug: 'bras', status: 'active' },
          { id: 'wt-panties', subcategoryId: 'w-innerwear', name: 'Panties & Seamless Briefs', slug: 'panties', status: 'active' },
          { id: 'wt-shapewear', subcategoryId: 'w-innerwear', name: 'Shapewear & Camisoles', slug: 'shapewear', status: 'active' },
        ]
      },
      {
        id: 'w-outerwear',
        categoryId: 'women',
        name: 'Outerwear & Jackets',
        slug: 'women-outerwear',
        status: 'active',
        types: [
          { id: 'wt-jackets', subcategoryId: 'w-outerwear', name: 'Jackets & Shrugs', slug: 'women-jackets', status: 'active' },
          { id: 'wt-sweaters', subcategoryId: 'w-outerwear', name: 'Cardigans & Sweaters', slug: 'women-sweaters', status: 'active' },
        ]
      }
    ]
  },
  {
    id: 'men',
    name: "Men's Fashion",
    slug: 'men',
    image: 'https://images.unsplash.com/photo-1597983073493-88cd35cf93b0?auto=format&fit=crop&w=800&q=80',
    banner: menBannerImg,
    status: 'active',
    sortOrder: 2,
    subcategories: [
      {
        id: 'm-ethnic',
        categoryId: 'men',
        name: 'Ethnic Wear',
        slug: 'men-ethnic',
        status: 'active',
        image: 'https://images.unsplash.com/photo-1597983073493-88cd35cf93b0?auto=format&fit=crop&w=800&q=80',
        types: [
          { id: 'mt-kurta', subcategoryId: 'm-ethnic', name: 'Kurta & Kurta Pajama', slug: 'men-kurta', status: 'active' },
          { id: 'mt-sherwani', subcategoryId: 'm-ethnic', name: 'Sherwani & Indo-Western', slug: 'sherwani', status: 'active' },
          { id: 'mt-nehru', subcategoryId: 'm-ethnic', name: 'Nehru Jacket & Bandhgala', slug: 'nehru-jackets', status: 'active' },
        ]
      },
      {
        id: 'm-western',
        categoryId: 'men',
        name: 'Casual Western Wear',
        slug: 'men-western',
        status: 'active',
        image: 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?auto=format&fit=crop&w=800&q=80',
        types: [
          { id: 'mt-tshirts', subcategoryId: 'm-western', name: 'T-Shirts (Polo, Oversized, Henley)', slug: 'men-tshirts', status: 'active' },
          { id: 'mt-shirts', subcategoryId: 'm-western', name: 'Casual Shirts (Printed, Denim, Checked)', slug: 'men-casual-shirts', status: 'active' },
          { id: 'mt-jeans', subcategoryId: 'm-western', name: 'Jeans & Denim Pants', slug: 'men-jeans', status: 'active' },
          { id: 'mt-chinos', subcategoryId: 'm-western', name: 'Chinos & Joggers', slug: 'men-chinos', status: 'active' },
          { id: 'mt-shorts', subcategoryId: 'm-western', name: 'Shorts & Bermudas', slug: 'men-shorts', status: 'active' },
        ]
      },
      {
        id: 'm-formal',
        categoryId: 'men',
        name: 'Formal & Office Wear',
        slug: 'men-formal',
        status: 'active',
        types: [
          { id: 'mt-formal-shirts', subcategoryId: 'm-formal', name: 'Formal Shirts', slug: 'men-formal-shirts', status: 'active' },
          { id: 'mt-formal-trousers', subcategoryId: 'm-formal', name: 'Formal Trousers', slug: 'men-formal-trousers', status: 'active' },
          { id: 'mt-suits', subcategoryId: 'm-formal', name: 'Suits & Blazers (2-Piece / 3-Piece)', slug: 'men-suits', status: 'active' },
        ]
      },
      {
        id: 'm-innerwear',
        categoryId: 'men',
        name: 'Innerwear & Loungewear',
        slug: 'men-innerwear',
        status: 'active',
        types: [
          { id: 'mt-briefs', subcategoryId: 'm-innerwear', name: 'Briefs & Trunks', slug: 'men-briefs', status: 'active' },
          { id: 'mt-boxers', subcategoryId: 'm-innerwear', name: 'Boxers & Vests', slug: 'men-boxers', status: 'active' },
          { id: 'mt-loungewear', subcategoryId: 'm-innerwear', name: 'Tracksuits & Pajamas', slug: 'men-loungewear', status: 'active' },
        ]
      }
    ]
  },
  {
    id: 'kids',
    name: "Kids' Fashion",
    slug: 'kids',
    image: '/images/kids_department_nano_banana.png',
    banner: kidsBannerImg,
    status: 'active',
    sortOrder: 3,
    subcategories: [
      {
        id: 'k-boys',
        categoryId: 'kids',
        name: "Boys' Clothing",
        slug: 'boys-clothing',
        status: 'active',
        types: [
          { id: 'kt-b-tshirts', subcategoryId: 'k-boys', name: 'T-Shirts & Shirts', slug: 'boys-tshirts', status: 'active' },
          { id: 'kt-b-ethnic', subcategoryId: 'k-boys', name: 'Kurta Pajama & Sherwanis', slug: 'boys-ethnic', status: 'active' },
          { id: 'kt-b-jeans', subcategoryId: 'k-boys', name: 'Jeans & Shorts', slug: 'boys-jeans', status: 'active' },
        ]
      },
      {
        id: 'k-girls',
        categoryId: 'kids',
        name: "Girls' Clothing",
        slug: 'girls-clothing',
        status: 'active',
        types: [
          { id: 'kt-g-dresses', subcategoryId: 'k-girls', name: 'Dresses & Frocks', slug: 'girls-dresses', status: 'active' },
          { id: 'kt-g-ethnic', subcategoryId: 'k-girls', name: 'Lehenga Choli & Kurtis', slug: 'girls-ethnic', status: 'active' },
          { id: 'kt-g-tops', subcategoryId: 'k-girls', name: 'Tops & Skirts', slug: 'girls-tops', status: 'active' },
        ]
      },
      {
        id: 'k-baby',
        categoryId: 'kids',
        name: 'Infant & Toddler (0-3Y)',
        slug: 'baby-infant',
        status: 'active',
        types: [
          { id: 'kt-rompers', subcategoryId: 'k-baby', name: 'Onesies & Rompers', slug: 'baby-onesies', status: 'active' },
          { id: 'kt-baby-sets', subcategoryId: 'k-baby', name: 'Swaddles & Sleepwear Sets', slug: 'baby-sets', status: 'active' },
        ]
      }
    ]
  },
  {
    id: 'undergarments',
    name: "Innerwear & Lingerie",
    slug: 'undergarments',
    image: '/images/innerwear_department_new.png',
    banner: innerwearBannerImg,
    status: 'active',
    sortOrder: 4,
    subcategories: [
      {
        id: 'u-women',
        categoryId: 'undergarments',
        name: "Women's Lingerie",
        slug: 'womens-lingerie',
        status: 'active',
        types: [
          { id: 'ut-bras', subcategoryId: 'u-women', name: 'Bras (Everyday, Sports, Strapless)', slug: 'women-bras', status: 'active' },
          { id: 'ut-panties', subcategoryId: 'u-women', name: 'Panties (Bikini, Seamless, Hipster)', slug: 'women-panties', status: 'active' },
          { id: 'ut-shapewear', subcategoryId: 'u-women', name: 'Shapewear & Tummy Control', slug: 'women-shapewear', status: 'active' },
          { id: 'ut-thermal-w', subcategoryId: 'u-women', name: 'Thermal Wear', slug: 'women-thermal', status: 'active' },
        ]
      },
      {
        id: 'u-men',
        categoryId: 'undergarments',
        name: "Men's Underwear",
        slug: 'mens-underwear',
        status: 'active',
        types: [
          { id: 'ut-briefs', subcategoryId: 'u-men', name: 'Briefs & Trunks', slug: 'men-briefs-trunks', status: 'active' },
          { id: 'ut-boxers', subcategoryId: 'u-men', name: 'Boxers & Vests', slug: 'men-boxers-vests', status: 'active' },
          { id: 'ut-thermal-m', subcategoryId: 'u-men', name: 'Thermal Wear', slug: 'men-thermal', status: 'active' },
        ]
      }
    ]
  }
];

export const initialBanners: Banner[] = [];

export const initialProducts: Product[] = generateFullCatalogProducts(initialCategories);

export const initialCoupons: Coupon[] = [
  {
    id: 'c1',
    code: 'WELCOME100',
    discountType: 'flat',
    value: 200,
    minOrderValue: 999,
    usageLimit: 500,
    usedCount: 42,
    expiryDate: '2026-12-31',
    isActive: true
  },
  {
    id: 'c2',
    code: 'TERRA15',
    discountType: 'percentage',
    value: 15,
    minOrderValue: 1499,
    maxDiscount: 500,
    usageLimit: 1000,
    usedCount: 189,
    expiryDate: '2026-12-31',
    isActive: true
  },
  {
    id: 'c3',
    code: 'FESTIVE25',
    discountType: 'percentage',
    value: 25,
    minOrderValue: 2499,
    maxDiscount: 1000,
    usageLimit: 200,
    usedCount: 78,
    expiryDate: '2026-11-30',
    isActive: true
  },
  {
    id: 'c4',
    code: 'DISCOUNT50',
    discountType: 'percentage',
    value: 50,
    minOrderValue: 499,
    maxDiscount: 2000,
    usageLimit: 5000,
    usedCount: 12,
    expiryDate: '2027-12-31',
    isActive: true
  },
  {
    id: 'c5',
    code: 'FESTIVE40',
    discountType: 'percentage',
    value: 40,
    minOrderValue: 999,
    maxDiscount: 1500,
    usageLimit: 5000,
    usedCount: 30,
    expiryDate: '2027-12-31',
    isActive: true
  }
];

export const initialReviews: Review[] = [
  {
    id: 'r1',
    productId: 'p1',
    customerName: 'Aishwarya R.',
    rating: 5,
    title: 'Breathtaking quality & rich color!',
    comment: 'The Banarasi chiffon saree exceeded my expectations! The terracotta tone is so elegant and unique compared to standard sarees. The drape is lightweight yet feels ultra luxurious.',
    isVerifiedPurchase: true,
    status: 'approved',
    createdAt: '2026-07-28T14:30:00Z'
  },
  {
    id: 'r2',
    productId: 'p5',
    customerName: 'Rohan Mehta',
    rating: 5,
    title: 'Super soft cotton knit fit',
    comment: 'Fits like a charm. The knit texture gives it a premium Italian feel without being too hot. Perfect for summer/monsoon wear.',
    isVerifiedPurchase: true,
    status: 'approved',
    createdAt: '2026-07-29T09:15:00Z'
  }
];
